import cn from "classnames";
import { useTranslation } from "react-i18next";
import { useFormikContext } from "formik";

import {
  ClientParametersProps,
  ClientParametersFormProps,
} from "./ClientParameters.types";

import { CALCULATOR_TYPES } from "constants/calculator";

import { Typography } from "shared/ui/components/Typography";

import { CalculatorFormikField } from "components/CalculatorFormikField";
import { InputField, SelectField } from "components/FormikFields";

import styles from "./ClientParameters.module.scss";

const Fields = ({
  t,
  familyTaxableIncomeOptions,
  splitTaxableIncomeOptions,
  calculatorType,
}: ClientParametersFormProps) => {
  const { values } = useFormikContext<any>();

  const isMarried = values.client?.hasSpouse?.value;

  return (
    <div
      className={cn(styles.fields, {
        [styles.fieldsStandard]: calculatorType === CALCULATOR_TYPES.BASIC,
      })}
    >
      <CalculatorFormikField
        name="client.hasSpouse"
        component={SelectField}
        controlProps={{
          label: t("calculator_form.client_parameters.marital_status"),
          variant: "horizontal",
          className: styles.fieldDesktop,
        }}
        componentProps={{
          size: "small",
          options: familyTaxableIncomeOptions,
          withoutBorder: true,
          controlClassName: styles.selectField
        }}
      />
      <CalculatorFormikField
        name="client.taxableIncomeSpouseOne"
        component={InputField}
        controlProps={{
          label: isMarried
            ? t("calculator_form.client_parameters.taxable_income_spouse_one")
            : t("calculator_form.client_parameters.your_taxable_income"),
          variant: "horizontal",
          className: styles.fieldDesktop,
        }}
        componentProps={{
          type: "localeDe",
          placeholder: "0",
          endAdornment: <Typography className={styles.currency} variant="bodyM" weight="medium">€</Typography>,
          withoutBorder: true,
          fieldClassName: styles.input,
          className: styles.inputWrapper
        }}
      />
      {isMarried && (
        <CalculatorFormikField
          name="client.taxableIncomeSpouseTwo"
          component={InputField}
          controlProps={{
            label: t("calculator_form.client_parameters.taxable_income_spouse_two"),
            variant: "horizontal",
            className: styles.fieldDesktop,
          }}
          componentProps={{
            type: "localeDe",
            placeholder: "0",
            endAdornment: <Typography className={styles.currency} variant="bodyM" weight="medium">€</Typography>,
            withoutBorder: true,
            fieldClassName: styles.input,
            className: styles.inputWrapper
          }}
        />
      )}
      {isMarried && (
        <CalculatorFormikField
          name="client.splitTaxableIncome"
          component={SelectField}
          controlProps={{
            label: t("calculator_form.client_parameters.split_taxable_income"),
            variant: "horizontal",
            className: styles.fieldDesktop,
          }}
          componentProps={{
            size: "small",
            options: splitTaxableIncomeOptions,
            withoutBorder: true,
            controlClassName: styles.selectField,
          }}
        />
      )}
    </div>
  );
};

const ClientParametersDesktop = ({
  t,
  familyTaxableIncomeOptions,
  splitTaxableIncomeOptions,
  calculatorType,
}: ClientParametersFormProps) => {
  return (
    <div className={styles.rootDesktop}>
      <Fields
        t={t}
        familyTaxableIncomeOptions={familyTaxableIncomeOptions}
        splitTaxableIncomeOptions={splitTaxableIncomeOptions}
        calculatorType={calculatorType}
      />
    </div>
  );
};

const ClientParametersMobile = ({
  t,
  familyTaxableIncomeOptions,
  splitTaxableIncomeOptions,
  calculatorType,
}: ClientParametersFormProps) => {
  return (
    <div className={styles.rootMobile}>
      <div className={cn(styles.header, {[styles.standardHeader]: calculatorType === CALCULATOR_TYPES.BASIC})}>
        <Typography
          className={styles.headerName}
          variant="bodyS"
          component="span"
        >
          {t("calculator_form.client_parameters.title")}
        </Typography>
      </div>
      <Fields
        t={t}
        familyTaxableIncomeOptions={familyTaxableIncomeOptions}
        splitTaxableIncomeOptions={splitTaxableIncomeOptions}
        calculatorType={calculatorType}
      />
    </div>
  );
};

const ClientParameters = ({
  activeTab,
  isDesktop,
  calculatorType,
  familyTaxableIncomeOptions,
  splitTaxableIncomeOptions,
}: ClientParametersProps) => {
  const { t } = useTranslation();


  if (calculatorType !== CALCULATOR_TYPES.BASIC && activeTab !== "client") {
    return null;
  }

  if (!isDesktop) {
    return (
      <ClientParametersMobile
        t={t}
        familyTaxableIncomeOptions={familyTaxableIncomeOptions}
        splitTaxableIncomeOptions={splitTaxableIncomeOptions}
        calculatorType={calculatorType}
      />
    );
  }

  return (
    <ClientParametersDesktop
      t={t}
      familyTaxableIncomeOptions={familyTaxableIncomeOptions}
      splitTaxableIncomeOptions={splitTaxableIncomeOptions}
      calculatorType={calculatorType}
    />
  );
};

export default ClientParameters;
